import { useState, type FormEvent, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, CheckCircle2, Plus, Send, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  createChannel,
  deleteChannel,
  listChannels,
  testChannel,
  type ChannelDto,
  type CreateChannelInput,
  type SlackConfig,
  type TelegramConfig,
} from '../api/channels.api';
import { Spinner } from '../components/Spinner';
import { Modal } from './Servers';
import { ConfirmDialog, useConfirm } from '../components/ConfirmDialog';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function apiErrorMessage(err: any, fallback: string): string {
  return err?.response?.data?.error?.message ?? fallback;
}

export function Channels() {
  const qc = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const confirm = useConfirm();

  const { data: channels, isLoading } = useQuery({
    queryKey: ['channels'],
    queryFn: listChannels,
  });

  const create = useMutation({
    mutationFn: createChannel,
    onSuccess: (channel) => {
      qc.invalidateQueries({ queryKey: ['channels'] });
      qc.invalidateQueries({ queryKey: ['rules'] });
      setShowAdd(false);
      toast.success(`Canale "${channel.name}" creato`);
    },
    onError: (err) => toast.error(apiErrorMessage(err, 'Creazione non riuscita')),
  });

  const del = useMutation({
    mutationFn: deleteChannel,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['channels'] });
      toast.success('Canale eliminato');
    },
    onError: (err) => toast.error(apiErrorMessage(err, 'Eliminazione non riuscita')),
  });

  const test = useMutation({
    mutationFn: testChannel,
    onSuccess: ({ result, channel }) => {
      qc.invalidateQueries({ queryKey: ['channels'] });
      if (result.success) {
        toast.success(`Messaggio di test inviato a ${channel.name}`);
      } else {
        toast.error('Test canale fallito', {
          description: result.error ?? undefined,
        });
      }
    },
    onError: (err) => toast.error(apiErrorMessage(err, 'Test canale fallito')),
  });

  return (
    <div className="p-5 md:p-10 max-w-7xl mx-auto space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-bold text-3xl text-ink">Canali di notifica</h1>
          <p className="text-sm text-ink-muted mt-1">
            Telegram e Slack — dove mandare gli alert.
          </p>
        </div>
        <button onClick={() => setShowAdd(true)} className="ixam-btn">
          <Plus className="w-4 h-4" />
          Aggiungi canale
        </button>
      </div>

      {isLoading ? (
        <div className="flex items-center gap-3 text-ink-muted">
          <Spinner size={20} /> Caricamento…
        </div>
      ) : (
        <div className="ixam-card overflow-x-auto">
          <table className="w-full min-w-[720px]">
            <thead className="bg-paper-soft text-xs uppercase tracking-wide text-ink-faint">
              <tr>
                <th className="text-left px-5 py-3 font-medium">Nome</th>
                <th className="text-left px-5 py-3 font-medium">Tipo</th>
                <th className="text-left px-5 py-3 font-medium">Stato</th>
                <th className="text-left px-5 py-3 font-medium">Ultimo test</th>
                <th className="px-5 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line-soft">
              {channels?.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-center py-16 text-ink-muted">
                    Nessun canale. Aggiungine uno per iniziare a ricevere notifiche.
                  </td>
                </tr>
              )}
              {channels?.map((c) => (
                <tr key={c._id} className="hover:bg-paper-soft/40">
                  <td className="px-5 py-3.5 font-medium text-ink">{c.name}</td>
                  <td className="px-5 py-3.5">
                    <TypeBadge type={c.type} />
                  </td>
                  <td className="px-5 py-3.5 text-sm">
                    {c.enabled ? (
                      <span className="text-sev-ok">attivo</span>
                    ) : (
                      <span className="text-ink-faint">disattivato</span>
                    )}
                  </td>
                  <td className="px-5 py-3.5 text-sm">
                    <TestStatusInline channel={c} />
                  </td>
                  <td className="px-5 py-3.5 text-right whitespace-nowrap">
                    <div className="inline-flex items-center gap-1">
                      <button
                        title="Invia messaggio di test"
                        onClick={() => test.mutate(c._id)}
                        disabled={test.isPending}
                        className="ixam-icon-btn text-accent"
                      >
                        <Send className="w-4 h-4" />
                      </button>
                      <button
                        title="Elimina canale"
                        onClick={() =>
                          confirm.ask({
                            title: `Eliminare "${c.name}"?`,
                            body: 'Le regole che instradano su questo canale resteranno ma non avranno destinazione attiva finché non le aggiorni.',
                            confirmLabel: 'Elimina',
                            destructive: true,
                            onConfirm: () => del.mutate(c._id),
                          })
                        }
                        className="ixam-icon-btn text-sev-critical"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showAdd && (
        <AddChannelModal
          onClose={() => setShowAdd(false)}
          onSave={(input) => create.mutate(input)}
          loading={create.isPending}
          error={create.error}
        />
      )}

      <ConfirmDialog request={confirm.request} onClose={confirm.dismiss} />
    </div>
  );
}

function TypeBadge({ type }: { type: ChannelDto['type'] }) {
  const styles: Record<ChannelDto['type'], string> = {
    telegram: 'bg-sev-info-soft text-sev-info border-sev-info/30',
    slack: 'bg-accent-soft text-accent-ink border-accent/30',
    email: 'bg-paper-soft text-ink-faint border-line',
    webhook: 'bg-paper-soft text-ink-faint border-line',
  };
  return (
    <span
      className={`inline-block px-2 py-0.5 text-xs font-medium rounded-md border ${styles[type]} capitalize`}
    >
      {type}
    </span>
  );
}

function TestStatusInline({ channel }: { channel: ChannelDto }) {
  const status = channel.testStatus;
  if (!status.lastTestAt) {
    return <span className="text-ink-faint italic">mai testato</span>;
  }
  const when = new Date(status.lastTestAt).toLocaleString('it-IT');
  if (status.lastTestSuccess) {
    return (
      <span className="inline-flex items-center gap-1.5 text-sev-ok">
        <CheckCircle2 className="w-3.5 h-3.5" />
        OK <span className="text-ink-faint ml-1 text-xs">({when})</span>
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1.5 text-sev-critical"
      title={status.lastTestError ?? ''}
    >
      <AlertCircle className="w-3.5 h-3.5" />
      Fallito <span className="text-ink-faint ml-1 text-xs">({when})</span>
    </span>
  );
}

function AddChannelModal({
  onClose,
  onSave,
  loading,
  error,
}: {
  onClose: () => void;
  onSave: (s: CreateChannelInput) => void;
  loading: boolean;
  error: unknown;
}) {
  const [type, setType] = useState<'telegram' | 'slack'>('telegram');
  const [name, setName] = useState('');
  const [tgConfig, setTgConfig] = useState<TelegramConfig>({ botToken: '', chatId: '' });
  const [slackConfig, setSlackConfig] = useState<SlackConfig>({ webhookUrl: '' });

  function submit(e: FormEvent) {
    e.preventDefault();
    if (type === 'telegram') {
      onSave({ name, type: 'telegram', config: tgConfig });
    } else {
      onSave({ name, type: 'slack', config: slackConfig });
    }
  }

  const errMsg = error
    ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ((error as any)?.response?.data?.error?.message ?? 'Salvataggio non riuscito')
    : null;

  return (
    <Modal title="Nuovo canale" onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <Field label="Nome">
          <input
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Telegram — Allarmi produzione"
            className="ixam-input"
          />
        </Field>
        <Field label="Tipo">
          <select
            value={type}
            onChange={(e) => setType(e.target.value as 'telegram' | 'slack')}
            className="ixam-input"
          >
            <option value="telegram">Telegram</option>
            <option value="slack">Slack</option>
          </select>
        </Field>

        {type === 'telegram' ? (
          <>
            <Field label="Bot token">
              <input
                required
                type="password"
                value={tgConfig.botToken}
                onChange={(e) => setTgConfig({ ...tgConfig, botToken: e.target.value })}
                placeholder="123456:ABC-DEF…"
                className="ixam-input"
              />
            </Field>
            <Field label="Chat ID">
              <input
                required
                value={tgConfig.chatId}
                onChange={(e) => setTgConfig({ ...tgConfig, chatId: e.target.value })}
                placeholder="-1001234567890"
                className="ixam-input"
              />
            </Field>
            <div className="text-xs text-ink-muted px-3 py-2.5 bg-paper-soft rounded-lg leading-relaxed">
              Crea un bot via @BotFather su Telegram, copia il token, poi
              avvia il bot nel canale o gruppo target. Per ottenere il{' '}
              <code className="text-ink">chat_id</code> usa @userinfobot.
            </div>
          </>
        ) : (
          <Field label="Webhook URL">
            <input
              required
              type="url"
              value={slackConfig.webhookUrl}
              onChange={(e) => setSlackConfig({ ...slackConfig, webhookUrl: e.target.value })}
              placeholder="https://hooks.slack.com/services/T00/B00/XXX"
              className="ixam-input"
            />
          </Field>
        )}

        {errMsg && (
          <div className="px-3 py-2 bg-sev-critical-soft border border-sev-critical/30 rounded-lg text-xs text-sev-critical">
            {errMsg}
          </div>
        )}
        <div className="flex gap-2 pt-2">
          <button type="button" onClick={onClose} className="ixam-btn-ghost flex-1 justify-center">
            Annulla
          </button>
          <button type="submit" disabled={loading} className="ixam-btn flex-1 justify-center">
            {loading ? 'Salvataggio…' : 'Salva canale'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <div className="text-xs font-medium text-ink mb-1.5">{label}</div>
      {children}
    </label>
  );
}
